import { ChiptaElement, html, render, css, unsafeCSS } from 'chipta-libs/main.js';
import { litStyle } from 'lit-element-style';
import { themeData, cssThemeData } from 'chipta-libs/utils/theme-data.js';
import './font-awesome.js';


function getButtonStyles() {

  const hoverStyle = cssThemeData('button.hover', 'filter: brightness(115%);');
  const focusStyle = cssThemeData('button.focus', hoverStyle);
  const activeStyle = cssThemeData('button.active', focusStyle);

  const baseStyles = css`

    :host {
      display: inline-block;
    }

    :host([wide]) {
      width: 100%;
    }

    * {
      box-sizing: border-box;
    }

    #button,
    #container ::slotted(button) {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
      cursor: pointer;
      outline: none;
      ${cssThemeData('button.base')};
      overflow: hidden;
    }

    :host([wide]) #button,
    :host([wide]) #container ::slotted(button) {
      width: 100%;
    }

    #container ::slotted(button:disabled) {
      cursor: not-allowed;
      ${cssThemeData('button.disabled', 'opacity: 0.5;')};
    }

    #button:hover,
    #container ::slotted(button:hover) {
      ${hoverStyle};
    }

    #button:focus,
    #container ::slotted(button:focus) {
      ${focusStyle};
    }

    #button:active,
    #container ::slotted(button:active) {
      ${activeStyle};
    }

  `;

  function getButtonSizeStyles() {

    const buttonSizes = Object.keys(themeData('button.size', {}));

    return buttonSizes.map(size => {

      return css`

        #button[size=${unsafeCSS(size)}],
        #container[size=${unsafeCSS(size)}] ::slotted(button) {
          ${cssThemeData('button.size.' + size)};
        }

        @media screen and (max-width: 500px) {
          #button[size=${unsafeCSS(size)}],
          #container[size=${unsafeCSS(size)}] ::slotted(button) {
            ${cssThemeData('button.size.' + size + '_smallscreen')};
          }
        }

      `;

    });

  }


  function getButtonTypeStyles() {

    const buttonTypes = Object.keys(themeData('button.type', {}));

    return buttonTypes.map(type => {

      const hoverStyle = cssThemeData('button.type.' + type + '.hover');
      const focusStyle = cssThemeData('button.type.' + type + '.focus', hoverStyle);
      const activeStyle = cssThemeData('button.type.' + type + '.active', focusStyle);

      return css`

        #button[type=${unsafeCSS(type)}],
        #container[type=${unsafeCSS(type)}] ::slotted(button) {
          ${cssThemeData('button.type.' + type + '.base')};
        }

        #button[type=${unsafeCSS(type)}]:hover,
        #container[type=${unsafeCSS(type)}] ::slotted(button:hover) {
          ${hoverStyle};
        }

        #button[type=${unsafeCSS(type)}]:focus,
        #container[type=${unsafeCSS(type)}] ::slotted(button:focus) {
          ${focusStyle};
        }

        #button[type=${unsafeCSS(type)}]:active,
        #container[type=${unsafeCSS(type)}] ::slotted(button:active) {
          ${activeStyle};
        }

      `;

    });

  }


  return litStyle([
    baseStyles,
    getButtonSizeStyles(),
    getButtonTypeStyles()
  ]);

}

const buttonStyles = getButtonStyles();


export class StyledButton extends buttonStyles(ChiptaElement) {

  static get properties() {
    return {
      size: {type: String},
      type: {type: String},
      icon: {type: String},
      iconTemplate: {type: Object},
      iconAlign: {type: String},
      method: {type: String},
      saving: {type: Boolean},
      saved: {type: Boolean},
      loading: {type: Boolean},
      loaded: {type: Boolean},
    };
  }

  constructor() {
    super();
    this.size = 'normal';
    this.type = 'normal';
    this.method = null;
    this.icon = this.defaultIcon;
    this.tempIcon = null;
    this.iconTemplate = null;
    this.didFirstUpdate = false;
    this.reEnableButtonTimeout = null;
  }

  static get observedAttributes() {
    return ['icon', 'icon-template', 'icon-align', 'method', 'saving', 'saved', 'loading', 'loaded'];
  }

  firstUpdated() {
    this.didFirstUpdate = true;
  }

  updated() {

    if (!this.method && this.hasAttribute('method')) {
      this.setMethod(this.getAttribute('method'));
    }

    if (!this.icon && this.hasAttribute('icon')) {
      this.setIcon(this.getAttribute('icon'));
    }

    if (!this.iconAlign && this.hasAttribute('icon-align')) {
      this.iconAlign = this.getAttribute('icon-align');
    }

    if (!this.iconTemplate && this.hasAttribute('icon-template')) {
      this.iconTemplate = this.getAttribute('icon-template');
    }

    this._initIcon();

  }

  attributeChangedCallback(attr, _oldValue, newValue) {

    if (['saving', 'saved', 'loading', 'loaded'].indexOf(attr) !== -1) {

      this.setButtonDisabledState();

      if (this.hasAttribute('saving') || this.hasAttribute('loading')) {
        this.setLoadingIcon();
      } else if (this.hasAttribute('saved') || this.hasAttribute('loaded')) {
        this.setSavedIcon();
      } else {
        this.removeTempIcon();
      }

    } else if (attr === 'icon') {
      this.setIcon(newValue);
    } else if (attr === 'icon-template') {
      this.setIconTemplate(newValue);
    } else if (attr === 'method') {
      this.setMethod(newValue);
    }

    this._initIcon();

  }

  render() {
    if (this.hasAttribute('href')) {
      return this.linkButtonContents;
    } else {
      return this.realButtonContents;
    }
  }

  get realButtonContents() {
    return html`
      <div
        id="container"
        size=${this.getButtonSize()}
        type=${this.getButtonType()}
        ?wide=${this.hasAttribute('wide')}
      >
        <slot @slotchange=${this.handleSlotChange}></slot>
      </div>
    `;
  }

  get linkButtonContents() {
    return html`
      <a
        id="button"
        size=${this.getButtonSize()}
        type=${this.getButtonType()}
        href=${this.getAttribute('href')}
        target=${this.getAttribute('target') || '_self'}
        ?wide=${this.hasAttribute('wide')}
      >
        <slot @slotchange=${this.handleSlotChange}></slot>
      </a>
    `;
  }

  get defaultIcon() {
    return this.getAttribute('icon');
  }

  get currentIcon() {
    return this.tempIcon || this.icon;
  }

  get buttonEl() {

    if (!this.didFirstUpdate) {
      return null;
    }

    if (this.hasAttribute('href')) {
      return this.el('#button');
    } else {
      return this.querySelector('button');
    }

  }

  getButtonSize() {
    return this.getAttribute('size') || this.size || '';
  }

  getButtonType() {
    return this.getAttribute('type') || this.type || '';
  }

  handleSlotChange() {
    this.setButtonDisabledState();
    this._initIcon();
  }

  /**
   * Set `<button>` element to `disabled` when it's loading or saving
   *
   * This prevents double form submits that cause issues like CSRF errors or
   * double API requests causing duplicate records or integrity errors.
   */
  setButtonDisabledState() {

    if (this.reEnableButtonTimeout) {
      clearTimeout(this.reEnableButtonTimeout);
    }

    if (!this.buttonEl) {
      return;
    }

    if (this.loading || this.saving) {

      this.buttonEl.setAttribute('disabled', '');

      // Re-enable the button again after 2 seconds. This prevents double
      // clicks, but allows the user to re-click the button even if the request
      // hasn't finished.
      this.reEnableButtonTimeout = setTimeout(() => {
        if (this.buttonEl) {
          this.buttonEl.removeAttribute('disabled');
        }
      }, 2000);

    } else {
      this.buttonEl.removeAttribute('disabled');
    }

  }

  setLoadingIcon() {
    this.setTempIcon('circle-notch');
  }

  setSavedIcon() {
    this.setTempIcon('check');
  }

  setIcon(name, align=null) {
    this.icon = name;
    this.iconTemplate = null;
    if (align) this.iconAlign = align;
  }

  setTempIcon(name) {
    this.tempIcon = name;
  }

  removeTempIcon() {
    this.tempIcon = null;
  }

  setIconTemplate(iconTemplate, align=null) {
    this.iconTemplate = iconTemplate;
    this.icon = null;
    if (align) this.iconAlign = align;
  }

  _initIcon() {

    this._removeIcon();

    if (!this.currentIcon && !this.iconTemplate) {
      return;
    }

    if (!this.buttonEl) {
      return;
    }

    const iconContainerEl = this.getIconContainerEl();
    const template = this.getIconTemplate();
 
    render(template, iconContainerEl);

    if (this.iconAlign === 'left') {
      this.buttonEl.insertBefore(iconContainerEl, this.buttonEl.firstChild);
    } else {
      this.buttonEl.appendChild(iconContainerEl);
    }

    this.buttonEl.appendChild(this.getContainerStyleEl());

  }

  /**
   * Returns the icon container element, containing the icon.
   */
  getIconContainerEl() {

    const iconContainerEl = document.createElement('span');

    iconContainerEl.setAttribute('id', 'icon-container');
    iconContainerEl.style.display = 'inline-flex';
    iconContainerEl.style.flexDirection = 'column';
    iconContainerEl.style.justifyContent = 'center';
    iconContainerEl.style.height = this.getIconContainerHeightCss();

    return iconContainerEl;

  }

  getContainerStyleEl() {

    const getStyles = () => {
      if (this.iconAlign === 'left') {
        return cssThemeData(`button.size.${this.getButtonSize()}.iconLeft`);
      } else {
        return cssThemeData(`button.size.${this.getButtonSize()}.iconRight`);
      }
    };

    const styleSheet = document.createElement('style');

    styleSheet.setAttribute('id', 'icon-container-styles');
    styleSheet.innerText = css`#icon-container { ${getStyles()} }`;

    return styleSheet;

  }

  /**
   * Returns a CSS value string for the width/height of the icon container
   */
  getIconContainerHeightCss() {
    // `buttonLineHeight` can be a string like `normal` or `10.24px` for
    // example. Here we check if the first character is a number. If it's not
    // a number (`NaN`), then we calculate the icon container height based on
    // the button height, padding and border width. Else we use the line height
    // to determine the icon container height.

    const computedStyle = window.getComputedStyle(this.buttonEl);
    const buttonLineHeight = computedStyle.lineHeight;

    if (isNaN(buttonLineHeight[0])) {

      return (
        'calc('
          + computedStyle.height + ' - '
          + computedStyle.paddingTop + ' - ' + computedStyle.paddingBottom + ' - '
          + computedStyle.borderWidth + ' - ' + computedStyle.borderWidth
        + ')'
      );

    } else {
      return buttonLineHeight;
    }

  }

  /**
   * Returns the `html` template for the icon
   */
  getIconTemplate() {

    if (this.iconTemplate) {
      // For using a custom template for the icon.
      return this.iconTemplate;
    } else {
      // Font awesome icon corresponding to the name in `this.icon`

      // Explicitly set the icon font size to the same as the `this.buttonEl`,
      // because the CSS inheritance doesn't get prioritized through styling
      // beyond <slot> elements.
      const iconFontSize = window.getComputedStyle(this.buttonEl).fontSize;

      return html`
        <font-awesome
          id="icon"
          icon=${this.currentIcon}
          icon-style="custom"
          style="font-size: ${iconFontSize}"
        ></font-awesome>
      `;

    }

  }

  setMethod(method) {

    this.method = method;

    const methodData = themeData('button.method.' + method, {});

    if ('type' in methodData && !this.hasAttribute('type')) {
      this.type = methodData.type;
    }

    if ('icon' in methodData && !this.hasAttribute('icon')) {
      this.setIcon(methodData.icon, methodData.icon_align);
    }

  }

  _removeIcon() {

    const iconEl = (() => {
      if (this.hasAttribute('href')) {
        return this.el('#icon-container');
      } else {
        return this.querySelector('#icon-container');
      }
    })();

    if (iconEl) iconEl.remove();

    const iconContainerStylesEl = (() => {
      if (this.hasAttribute('href')) {
        return this.el('#icon-container-styles');
      } else {
        return this.querySelector('#icon-container-styles');
      }
    })();

    if (iconContainerStylesEl) iconContainerStylesEl.remove();

  }

  get loading() {
    return this.hasAttribute('loading');
  }

  get saving() {
    return this.hasAttribute('saving');
  }

  set loading(bool) {
    if (bool) {
      this.setAttribute('loading', '');
    } else {
      this.removeAttribute('loading');
    }
  }

}


customElements.define('styled-button', StyledButton);
